<div class="app-main__inner">

    <div class="app-page-title">
        <div class="page-title-wrapper">
            <div class="page-title-heading">
                <div class="page-title-icon">
                    <i class="pe-7s-plugin icon-gradient bg-tempting-azure"></i>
                </div>
                <div>
                    WhatsApp Flows
                    <div class="page-title-subheading">
                        Create and manage interactive WhatsApp Forms for surveys, sign-ups, and more.
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create Flow card -->
    <div class="card mb-4">
        <div class="card-header d-flex align-items-center gap-2">
            <i class="pe-7s-plus me-1"></i>
            <span class="fw-semibold">Create New Flow</span>
        </div>
        <div class="card-body">
            <div *ngIf="createResult" class="alert py-2 px-3 mb-3"
                [ngClass]="createSuccess ? 'alert-success' : 'alert-danger'">
                <i [ngClass]="createSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
                {{ createResult }}
            </div>
            <div class="row g-3 align-items-start">
                <div class="col-md-5">
                    <label class="form-label small fw-semibold mb-1">Flow Name</label>
                    <input type="text" class="form-control" placeholder="e.g. Employee Satisfaction Survey"
                        [(ngModel)]="createName" (keyup.enter)="createFlow()">
                </div>
                <div class="col-md-5">
                    <label class="form-label small fw-semibold mb-1">
                        Categories
                        <span class="text-muted fw-normal">(select one or more)</span>
                    </label>
                    <div class="d-flex flex-wrap gap-2">
                        <div *ngFor="let cat of categories" class="form-check-inline m-0">
                            <label class="category-chip"
                                [class.category-chip--active]="isCategorySelected(cat)"
                                (click)="toggleCategory(cat)">
                                {{ cat }}
                            </label>
                        </div>
                    </div>
                </div>
                <div class="col-md-2 d-flex align-items-end" style="padding-top: 1.6rem;">
                    <button class="btn btn-primary w-100" (click)="createFlow()"
                        [disabled]="!createName.trim() || isCreating">
                        <span *ngIf="isCreating" class="spinner-border spinner-border-sm me-1"></span>
                        <i *ngIf="!isCreating" class="pe-7s-plus me-1"></i>
                        {{ isCreating ? 'Creating...' : 'Create' }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Flows list -->
    <div class="card mb-4">
        <div class="card-header d-flex align-items-center gap-2">
            <i class="pe-7s-plugin me-1"></i>
            <span class="fw-semibold">Your Flows</span>
            <span *ngIf="flows.length > 0" class="badge bg-primary rounded-pill ms-auto">{{ flows.length }}</span>
            <button class="btn btn-sm btn-outline-secondary ms-auto" (click)="loadFlows()"
                [ngClass]="{ 'ms-auto': flows.length === 0 }">
                <span *ngIf="isLoadingFlows" class="spinner-border spinner-border-sm"></span>
                <i *ngIf="!isLoadingFlows" class="pe-7s-refresh"></i>
            </button>
        </div>
        <div class="card-body p-0">
            <div *ngIf="isLoadingFlows" class="text-center py-5 text-muted">
                <span class="spinner-border spinner-border-sm me-2"></span> Loading flows...
            </div>
            <div *ngIf="!isLoadingFlows && flows.length === 0" class="text-center py-5 text-muted small">
                No flows yet. Create one above.
            </div>
            <div *ngIf="!isLoadingFlows && flows.length > 0" class="table-responsive">
                <table class="table table-hover mb-0 align-middle">
                    <thead class="table-light">
                        <tr>
                            <th class="ps-3">Flow Name</th>
                            <th>Category</th>
                            <th>Status</th>
                            <th>Meta Flow ID</th>
                            <th>Created</th>
                            <th class="text-end pe-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let flow of flows; trackBy: trackById"
                            [class.table-active]="editingFlow?._id === flow._id">
                            <td class="ps-3 fw-semibold">{{ flow.flowName }}</td>
                            <td>
                                <span *ngFor="let cat of flow.categories" class="badge bg-light text-dark border me-1">
                                    {{ cat }}
                                </span>
                            </td>
                            <td>
                                <span class="badge" [ngClass]="statusClass(flow.status)">{{ flow.status }}</span>
                            </td>
                            <td>
                                <code class="text-muted small">{{ flow.metaFlowId }}</code>
                                <button class="btn btn-sm btn-link p-0 ms-1" title="Copy ID"
                                    (click)="copyFlowId(flow.metaFlowId)">
                                    <i [ngClass]="copiedId === flow.metaFlowId ? 'pe-7s-check text-success' : 'pe-7s-copy-file text-muted'"></i>
                                </button>
                            </td>
                            <td class="text-muted small">{{ flow.createdAt | date:'dd MMM yyyy' }}</td>
                            <td class="text-end pe-3">
                                <div class="d-flex gap-1 justify-content-end flex-wrap">

                                    <!-- Edit JSON — DRAFT only -->
                                    <button *ngIf="flow.status === 'DRAFT'"
                                        class="btn btn-sm btn-outline-secondary"
                                        title="Edit flow JSON"
                                        [disabled]="!!actionLoadingId"
                                        (click)="startEditJson(flow)">
                                        <i class="pe-7s-pen"></i> Edit JSON
                                    </button>

                                    <!-- Publish — DRAFT + clean JSON -->
                                    <button *ngIf="flow.status === 'DRAFT'"
                                        class="btn btn-sm btn-outline-success"
                                        title="{{ hasCleanJson(flow) ? 'Publish this flow' : 'Upload valid JSON first' }}"
                                        [disabled]="!!actionLoadingId || !hasCleanJson(flow)"
                                        (click)="publishFlow(flow)">
                                        <span *ngIf="actionLoadingId === flow._id" class="spinner-border spinner-border-sm"></span>
                                        <i *ngIf="actionLoadingId !== flow._id" class="pe-7s-upload"></i>
                                        Publish
                                    </button>

                                    <!-- Sync status -->
                                    <button class="btn btn-sm btn-outline-primary"
                                        title="Sync status from Meta"
                                        [disabled]="!!actionLoadingId"
                                        (click)="syncFlow(flow)">
                                        <span *ngIf="actionLoadingId === flow._id" class="spinner-border spinner-border-sm"></span>
                                        <i *ngIf="actionLoadingId !== flow._id" class="pe-7s-refresh"></i>
                                        Sync
                                    </button>

                                    <!-- Deprecate — PUBLISHED only -->
                                    <button *ngIf="flow.status === 'PUBLISHED'"
                                        class="btn btn-sm btn-outline-warning"
                                        title="Deprecate this flow"
                                        [disabled]="!!actionLoadingId"
                                        (click)="deprecateFlow(flow)">
                                        <span *ngIf="actionLoadingId === flow._id" class="spinner-border spinner-border-sm"></span>
                                        <i *ngIf="actionLoadingId !== flow._id" class="pe-7s-del-circle"></i>
                                        Deprecate
                                    </button>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- JSON Editor (shown when a flow is being edited) -->
    <div *ngIf="editingFlow" id="json-editor-section" class="card mb-4 border-primary">
        <div class="card-header d-flex align-items-center gap-2 bg-primary bg-opacity-10">
            <i class="pe-7s-code me-1 text-primary"></i>
            <span class="fw-semibold" style="color: white;">Editing JSON: {{ editingFlow.flowName }}</span>
            <span class="badge bg-secondary ms-2">{{ editingFlow.status }}</span>
            <button class="btn btn-sm btn-outline-secondary ms-auto" (click)="cancelEdit()">
                <i class="pe-7s-close me-1"></i> Close Editor
            </button>
        </div>
        <div class="card-body">

            <div *ngIf="uploadResult" class="alert py-2 px-3 mb-3"
                [ngClass]="uploadSuccess ? 'alert-success' : 'alert-warning'">
                <i [ngClass]="uploadSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
                {{ uploadResult }}
            </div>

            <div *ngIf="validationErrors.length > 0" class="alert alert-danger py-2 px-3 mb-3">
                <div class="fw-semibold mb-1">Validation Errors:</div>
                <ul class="mb-0 ps-3 small">
                    <li *ngFor="let err of validationErrors">
                        <strong>{{ err.error }}</strong>: {{ err.message }}
                        <span *ngIf="err.line_start">(line {{ err.line_start }})</span>
                    </li>
                </ul>
            </div>

            <div class="row g-4">

                <!-- Left: JSON Editor -->
                <div class="col-lg-7">
                    <textarea class="form-control font-monospace mb-3"
                        rows="20"
                        style="font-size: 0.82rem; line-height: 1.5; resize: vertical;"
                        [(ngModel)]="editJson"
                        placeholder="Paste your WhatsApp Flow JSON here...">
                    </textarea>

                    <div class="d-flex gap-2 align-items-center flex-wrap">
                        <button class="btn btn-primary" (click)="uploadJson()" [disabled]="!editJson.trim() || isUploading">
                            <span *ngIf="isUploading" class="spinner-border spinner-border-sm me-1"></span>
                            <i *ngIf="!isUploading" class="pe-7s-cloud-upload me-1"></i>
                            {{ isUploading ? 'Uploading...' : 'Upload to Meta' }}
                        </button>
                        <button class="btn btn-success"
                            *ngIf="uploadSuccess && validationErrors.length === 0"
                            (click)="publishFlow(editingFlow!)"
                            [disabled]="!!actionLoadingId">
                            <span *ngIf="actionLoadingId === editingFlow!._id" class="spinner-border spinner-border-sm me-1"></span>
                            <i *ngIf="actionLoadingId !== editingFlow!._id" class="pe-7s-upload me-1"></i>
                            Publish Flow
                        </button>
                        <span class="text-muted small ms-auto">
                            Upload JSON to validate, then publish.
                        </span>
                    </div>
                </div>

                <!-- Right: Phone Preview -->
                <div class="col-lg-5">
                    <div class="preview-panel">

                        <!-- Screen selector -->
                        <div class="preview-topbar">
                            <span class="preview-label">Preview</span>
                            <select class="preview-screen-select" [(ngModel)]="selectedScreenId"
                                *ngIf="previewScreens.length > 1">
                                <option *ngFor="let s of previewScreens" [value]="s.id">{{ s.id }}</option>
                            </select>
                            <span class="preview-screen-name" *ngIf="previewScreens.length === 1">
                                {{ previewScreens[0].id }}
                            </span>
                        </div>

                        <!-- Phone frame -->
                        <div class="phone-frame">

                            <!-- Status bar -->
                            <div class="phone-status-bar"></div>

                            <!-- WhatsApp header -->
                            <div class="phone-wa-header">
                                <span class="wa-close">✕</span>
                                <span class="wa-title">{{ currentPreviewScreen?.title || '—' }}</span>
                                <span class="wa-menu">⋮</span>
                            </div>

                            <!-- Body content -->
                            <div class="phone-body" *ngIf="currentPreviewScreen; else noPreview">
                                <ng-container *ngFor="let child of previewBodyChildren">
                                    <ng-container [ngSwitch]="child.type">

                                        <div *ngSwitchCase="'TextHeading'" class="flow-heading">
                                            {{ child.text }}
                                        </div>

                                        <div *ngSwitchCase="'TextSubheading'" class="flow-subheading">
                                            {{ child.text }}
                                        </div>

                                        <div *ngSwitchCase="'TextBody'" class="flow-text-body">
                                            {{ child.text }}
                                        </div>

                                        <div *ngSwitchCase="'TextCaption'" class="flow-caption">
                                            {{ child.text }}
                                        </div>

                                        <div *ngSwitchCase="'TextInput'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <input class="flow-input" [placeholder]="child.label" disabled>
                                        </div>

                                        <div *ngSwitchCase="'TextArea'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <textarea class="flow-textarea" rows="3" [placeholder]="child.label" disabled></textarea>
                                        </div>

                                        <div *ngSwitchCase="'DatePicker'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <div class="flow-date-row">
                                                <span>{{ child.label }}</span>
                                                <span>📅</span>
                                            </div>
                                        </div>

                                        <div *ngSwitchCase="'Dropdown'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <div class="flow-dropdown-row">
                                                <span>{{ child.label }}</span>
                                                <span class="flow-arrow">›</span>
                                            </div>
                                        </div>

                                        <div *ngSwitchCase="'RadioButtonsGroup'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <div *ngFor="let opt of dataSource(child)" class="flow-radio-item">
                                                <span class="flow-radio-circle"></span>
                                                <span>{{ opt.title }}</span>
                                            </div>
                                            <div *ngIf="dataSource(child).length === 0" class="flow-placeholder-text">
                                                (data-source options)
                                            </div>
                                        </div>

                                        <div *ngSwitchCase="'CheckboxGroup'" class="flow-field">
                                            <div class="flow-field-label">{{ child.label }}</div>
                                            <div *ngFor="let opt of dataSource(child)" class="flow-check-item">
                                                <span class="flow-checkbox-box"></span>
                                                <span>{{ opt.title }}</span>
                                            </div>
                                            <div *ngIf="dataSource(child).length === 0" class="flow-placeholder-text">
                                                (data-source options)
                                            </div>
                                        </div>

                                        <div *ngSwitchCase="'OptIn'" class="flow-optin">
                                            <span class="flow-checkbox-box"></span>
                                            <span class="flow-caption">{{ child.label }}</span>
                                        </div>

                                        <div *ngSwitchCase="'Image'" class="flow-image-placeholder">
                                            <i class="pe-7s-photo"></i> Image
                                        </div>

                                        <div *ngSwitchCase="'EmbeddedLink'" class="flow-caption" style="color:#0d6efd;">
                                            {{ child.text || child.label }}
                                        </div>

                                    </ng-container>
                                </ng-container>
                            </div>

                            <ng-template #noPreview>
                                <div class="phone-body phone-empty">
                                    <span>Enter valid JSON<br>to see preview</span>
                                </div>
                            </ng-template>

                            <!-- Footer -->
                            <div class="phone-footer" *ngIf="previewFooter">
                                <div class="flow-footer-btn">{{ previewFooter.label || 'Submit' }}</div>
                                <div class="flow-managed-text">
                                    Managed by the business. <span style="color:#0d6efd;">Learn more</span>
                                </div>
                            </div>
                        </div>

                        <div class="preview-note">Rendering varies by device.</div>
                    </div>
                </div>

            </div><!-- /row -->
        </div>
    </div>

</div>
